import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Send, RotateCcw, Eye, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DraftControls — drop-in Save Draft / Publish / Discard / Preview toolbar.
 * Uses the generic /api/content/drafts/{draftKey} endpoints.
 *
 * Props:
 *  - draftKey (string, required): the content_drafts key (e.g. 'program', 'sponsorship', 'golf', 'speakers')
 *  - pageLabel (string): human-readable page name used in toasts/tooltips
 *  - previewPath (string, default '/'): path to open when clicking Preview (with ?preview=1 appended)
 *  - getSnapshot () -> object (optional): returns the CURRENT editor state as a JSON snapshot.
 *      If omitted, "Save Draft" uses the server-side snapshot-from-live handler instead.
 *  - onPublished (fn): called after successful publish
 *  - onDraftLoaded (fn(data)): called with the draft data once loaded, so the parent can hydrate its editor
 *  - hideLoadDraft (bool): if true, hides the "Load draft into editor" prompt
 */
export default function DraftControls({ draftKey, pageLabel, previewPath = '/', getSnapshot, onPublished, onDraftLoaded, hideLoadDraft }) {
  const token = () => localStorage.getItem('admin_token');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'save' | 'publish' | 'discard' | null

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/content/drafts/${draftKey}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (r.ok) {
        setDraft(await r.json());
      } else {
        setDraft(null);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [draftKey]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveDraft = async () => {
    setBusy('save');
    try {
      const snapshot = getSnapshot ? getSnapshot() : null;
      if (snapshot) {
        const r = await fetch(`${API}/api/content/drafts/${draftKey}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: snapshot }),
        });
        if (r.ok) {
          toast.success('Draft saved. Click Preview to see it.');
          setDraft(await r.json());
        } else {
          toast.error('Save draft failed');
        }
      } else {
        // snapshot-from-live: seeds a fresh draft from the current live state
        const r = await fetch(`${API}/api/content/drafts/${draftKey}/snapshot-from-live`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (r.ok) {
          toast.success('Draft initialized from current live state.');
          setDraft(await r.json());
        } else {
          toast.error('Save draft failed');
        }
      }
    } catch {
      toast.error('Save draft failed');
    }
    setBusy(null);
  };

  const publishDraft = async () => {
    if (!window.confirm(`Publish this draft to the live ${pageLabel || draftKey} page? This will overwrite the current live content.`)) return;
    setBusy('publish');
    try {
      const r = await fetch(`${API}/api/content/drafts/${draftKey}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (r.ok) {
        toast.success(`Published to live${pageLabel ? ` (${pageLabel})` : ''}`);
        setDraft(null);
        onPublished?.();
      } else {
        const data = await r.json().catch(() => ({}));
        toast.error(data.detail || 'Publish failed');
      }
    } catch {
      toast.error('Publish failed');
    }
    setBusy(null);
  };

  const discardDraft = async () => {
    if (!window.confirm(`Discard the ${pageLabel || draftKey} draft? Any unpublished changes will be lost.`)) return;
    setBusy('discard');
    try {
      const r = await fetch(`${API}/api/content/drafts/${draftKey}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (r.ok) {
        toast.success('Draft discarded');
        setDraft(null);
      }
    } catch {
      toast.error('Discard failed');
    }
    setBusy(null);
  };

  const loadIntoEditor = () => {
    if (!draft || !onDraftLoaded) return;
    onDraftLoaded(draft.data);
    toast.success('Loaded draft into editor. Save Draft to persist any further edits.');
  };

  const openPreview = () => {
    const path = previewPath || '/';
    const sep = path.includes('?') ? '&' : '?';
    window.open(`${path}${sep}preview=1`, '_blank');
  };

  const hasDraft = !!draft;
  const updatedRel = draft?.updated_at ? formatRelative(new Date(draft.updated_at)) : null;

  return (
    <div className="bg-white border rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`draft-controls-${draftKey}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <FileText className={`w-4 h-4 ${hasDraft ? 'text-amber-500' : 'text-gray-300'}`} />
        <div className="min-w-0">
          <div className="text-xs font-medium text-gray-700">{pageLabel || draftKey} draft</div>
          <div className="text-xs text-gray-500 truncate">
            {loading ? 'checking…' : hasDraft ? `Unpublished draft saved ${updatedRel}${draft.updated_by ? ` by ${draft.updated_by}` : ''}` : 'No unpublished draft'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && !hideLoadDraft && onDraftLoaded && (
          <Button size="sm" variant="outline" onClick={loadIntoEditor} data-testid={`draft-load-${draftKey}`}>Load draft</Button>
        )}
        <Button size="sm" variant="outline" onClick={openPreview} data-testid={`draft-preview-${draftKey}`}>
          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
        </Button>
        <Button
          size="sm"
          onClick={saveDraft}
          disabled={busy === 'save'}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid={`draft-save-${draftKey}`}
          title={getSnapshot ? 'Save current editor state as a draft' : 'Snapshot the current live state into a draft'}
        >
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
          Save as Draft
        </Button>
        {hasDraft && (
          <>
            <Button size="sm" onClick={publishDraft} disabled={busy === 'publish'} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`draft-publish-${draftKey}`}>
              {busy === 'publish' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Publish
            </Button>
            <Button size="sm" variant="outline" onClick={discardDraft} disabled={busy === 'discard'} className="text-red-600 hover:text-red-700" data-testid={`draft-discard-${draftKey}`}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Discard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function formatRelative(date) {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
